import { Set } from "immutable";
import { createSelector } from "reselect";

import syncApplications, { syncApplicationNamesForConstant } from "../../../constants/syncApplications";
import SyncCustomFieldOptions from "../../../constants/SyncCustomFieldOptions";
import { SYNC_NARRATIVE_FOLDER_IDENTIFIER } from "../../../constants/syncNarrativeFolderIdentifier";
import { doSetsIntersect } from "../../../utils/immutableExtensions";
import getIdsInTree from "../../../utils/tree/getIdsInTree";
import getSubTreeWithId from "../../../utils/tree/getSubTreeWithId";
import typeSelectors from "../../definition/types/selectors";
import syncSelectors from "../selectors";

const getSyncSettings = state => (state.sync ? state.sync.settings : null);

const getSyncEnabled = state => (state.sync && state.sync.settings ? getSyncSettings(state).enabled : false);
const getSyncApplication = state => getSyncSettings(state).app;
const getSyncApplicationName = state => syncApplicationNamesForConstant[getSyncApplication(state)];
const getSyncPath = state => getSyncSettings(state).path;
const getSyncNarrativeFolderId = state => getSyncSettings(state).narrativeFolderId;

const isFurtherConfigRequired = state => getSyncEnabled(state) && !getSyncNarrativeFolderId(state);

const getSyncCustomFieldIdsFromSettings = settings =>
  settings.fields
    .filter(f => f.customFieldId && f.customFieldId !== SyncCustomFieldOptions.NONE)
    .map(f => f.customFieldId)
    .toList();

const getSyncCustomFieldsFromSettings = settings =>
  settings.fields.filter(f => f.customFieldId && f.customFieldId !== SyncCustomFieldOptions.NONE);

const getSyncEntityFolders = state => (state.sync && state.sync.settings ? state.sync.settings.entityFolders : null);

const getSyncRootFolderIdsFromSettings = createSelector(
  // inputs
  settings => settings.entityFolders,
  // output
  entityFolders => {
    return entityFolders
      .map(f => f.folderId)
      .toList()
      .insert(0, SYNC_NARRATIVE_FOLDER_IDENTIFIER);
  }
);

const getSyncRootFolderIds = state =>
  state.sync && state.sync.settings ? getSyncRootFolderIdsFromSettings(state.sync.settings) : null;

const getExternalSyncRootFolderIdsFromSettings = createSelector(
  // inputs
  settings => settings.entityFolders,
  settings => settings.narrativeFolderId,
  // output
  (entityFolders, narrativeFolder) => {
    return entityFolders
      .map(f => f.folderId)
      .toList()
      .insert(0, narrativeFolder);
  }
);

const getExternalSyncRootFolderIds = state =>
  state.sync && state.sync.settings ? getExternalSyncRootFolderIdsFromSettings(state.sync.settings) : null;

const getSyncUseKeywordsForRelationships = state =>
  state.sync && state.sync.settings ? state.sync.settings.useKeywordsForRelationships : false;

const getSyncKeywordTypes = state =>
  getSyncUseKeywordsForRelationships(state) ? state.sync.settings.keywordTypes : Set();

const getSyncUseKeywordsForRelationshipsFromSettings = settings => settings.useKeywordsForRelationships;

const getSyncKeywordTypesFromSettings = settings =>
  getSyncUseKeywordsForRelationshipsFromSettings(settings) ? settings.keywordTypes : Set();

const getTypesFromSyncEntityFolder = entityFolder => {
  return Set([entityFolder.mainType, entityFolder.defaultDocumentType, entityFolder.defaultFolderType]).withMutations(
    entityTypes => {
      entityFolder.levels.forEach(l => {
        entityTypes.add(l.documentType).add(l.folderType);
      });
      return entityTypes;
    }
  );
};

const getEntityTypesFromSyncSettings = createSelector(
  // input
  getSyncSettings,
  // output
  syncSettings => {
    return Set().withMutations(entityTypes => {
      syncSettings.entityFolders.forEach(f => {
        entityTypes.union(getTypesFromSyncEntityFolder(f));
      });
      return entityTypes;
    });
  }
);

const getSelectedSyncTypeSet = createSelector(
  // input
  getEntityTypesFromSyncSettings,
  typeSelectors.getNarrativeTypeIds,
  // output
  (entityTypeIdsSet, narrativeTypeIds) => entityTypeIdsSet.union(narrativeTypeIds.toSet())
);

const isSyncSettingsValid = createSelector(
  // input
  getSyncSettings,
  syncSelectors.getExternalSyncRep,
  typeSelectors.getTypesById,
  // output
  (syncSettings, externalRep, typesById) => {
    let customFields = {};
    let types = Set();
    let isValid = true;
    let errorMessage = null;

    const externalDocumentTree = externalRep ? externalRep.documentTree : null;

    const syncApp = syncSettings.app;
    const syncAppName = syncApplicationNamesForConstant[syncApp];

    if (!syncSettings.narrativeFolderId) {
      return {
        isValid: false,
        errorMessage: "You must select a folder to sync Narrative"
      };
    }

    // For both Scrivener and Ulysses: make sure that the same folder is not selected
    // twice, and that one is not a child of another.
    if (syncSettings.entityFolders.find(f => f.folderId === syncSettings.narrativeFolderId)) {
      return {
        isValid: false,
        errorMessage: "Cannot sync narrative and entities to the same folder"
      };
    }

    // Get all folder IDs
    const folderIds = syncSettings.entityFolders
      .map(f => f.folderId)
      .toList()
      .push(syncSettings.narrativeFolderId)
      .toSet();

    // Now make that every selected folder exists in the externalRep
    const missingFolder =
      externalDocumentTree &&
      folderIds.find(fId => {
        // Get all IDs below this subtree
        const subtree = getSubTreeWithId(externalDocumentTree, fId);
        return !subtree;
      });

    if (missingFolder) {
      return {
        isValid: false,
        errorMessage: `A selected sync folder is missing in ${syncAppName}`
      };
    }

    // Now make sure that none are children of others
    const nestedFolder =
      externalDocumentTree &&
      folderIds.find(fId => {
        // Get all IDs below this subtree
        const subtreeIds = Set(getIdsInTree(getSubTreeWithId(externalDocumentTree, fId))).delete(fId);
        // Check for any overlap with folderIds
        return doSetsIntersect(subtreeIds, folderIds);
      });

    if (nestedFolder) {
      return {
        isValid: false,
        errorMessage: "Cannot sync to a folder inside another selected folder"
      };
    }

    // Ensure that a type is selected for each folder
    const typelessFolder = syncSettings.entityFolders.find(folder => !folder.mainType);
    if (typelessFolder) {
      return {
        isValid: false,
        errorMessage: "You must select an Item Type for each Entity Folder"
      };
    }

    // Also make sure that each type appears in only a single folder
    syncSettings.entityFolders.forEach(folder => {
      const myTypes = getTypesFromSyncEntityFolder(folder);
      const repeatedType = myTypes.find(t => types.has(t));
      if (repeatedType) {
        isValid = false;
        errorMessage = `Type ${typesById.get(repeatedType).label} cannot sync to multiple folders`;
      }
      types = types.union(myTypes);
    });

    // Otherwise, if we reach here, there are no other ways for Ulysses settings to be wrong.
    // Ulysses is always correct - there is no way to make it wrong!
    if (syncApp == syncApplications.ULYSSES) {
      return {
        isValid,
        errorMessage
      };
    }

    // Iterate through the fields and find any duplicates in selected folders or fields
    syncSettings.fields.forEach(field => {
      if (
        field.customFieldId &&
        field.customFieldId !== SyncCustomFieldOptions.NONE &&
        field.customFieldId !== SyncCustomFieldOptions.CREATE
      ) {
        if (customFields[field.customFieldId]) {
          isValid = false;
          errorMessage = `Cannot sync multiple values to the same custom field`;
        }
        customFields[field.customFieldId] = true;
      }
    });
    return {
      isValid,
      errorMessage
    };
  }
);

export default {
  getSyncSettings,
  getSyncEnabled,
  getSyncApplication,
  getSyncApplicationName,
  getSyncPath,
  getSyncNarrativeFolderId,
  isFurtherConfigRequired,
  getSyncCustomFieldIdsFromSettings,
  getSyncCustomFieldsFromSettings,
  getSyncEntityFolders,
  getSyncUseKeywordsForRelationships,
  getSyncUseKeywordsForRelationshipsFromSettings,
  getSyncKeywordTypes,
  getSyncKeywordTypesFromSettings,
  getSyncRootFolderIdsFromSettings,
  getSyncRootFolderIds,
  getExternalSyncRootFolderIds,
  isSyncSettingsValid,
  getTypesFromSyncEntityFolder,
  getEntityTypesFromSyncSettings,
  getSelectedSyncTypeSet
};
